import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, Field, SectionTitle, Pill } from "@/components/Bits";
import ProductBoard from "@/components/ProductBoard";
import NotesPanel from "@/components/NotesPanel";
import FilesPanel from "@/components/FilesPanel";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-lime";
const blank = { name: "", website: "", account_rep: "", email: "", phone: "", trade_account_number: "", notes: "", default_lead_time: "", shipping_notes: "", return_policy_notes: "", vendor_type: "", status: "Active" };

export function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState(blank);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/vendors").then((r) => setVendors(r.data)).catch(() => setVendors([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/vendors", form);
      toast.success("Vendor added");
      setForm(blank);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-10" data-testid="vendors-page">
      <PageHeader
        overline="Sourcing"
        title="Vendors"
        subtitle="Trade accounts, reps, lead times and policies."
        actions={
          <button data-testid="add-vendor-btn" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
            <Plus className="h-4 w-4" strokeWidth={1.5} /> Add Vendor
          </button>
        }
      />

      {open && (
        <form onSubmit={submit} className="card-surface grid gap-5 p-7 sm:grid-cols-2 lg:grid-cols-3" data-testid="vendor-form">
          <Field label="Vendor Name"><input data-testid="vendor-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} /></Field>
          <Field label="Vendor Type"><input data-testid="vendor-type-input" value={form.vendor_type} onChange={(e) => setForm({ ...form, vendor_type: e.target.value })} className={input} /></Field>
          <Field label="Website"><input data-testid="vendor-website-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={input} /></Field>
          <Field label="Account Rep"><input data-testid="vendor-rep-input" value={form.account_rep} onChange={(e) => setForm({ ...form, account_rep: e.target.value })} className={input} /></Field>
          <Field label="Email"><input data-testid="vendor-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} /></Field>
          <Field label="Phone"><input data-testid="vendor-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={input} /></Field>
          <Field label="Trade Account Number"><input data-testid="vendor-account-input" value={form.trade_account_number} onChange={(e) => setForm({ ...form, trade_account_number: e.target.value })} className={`num ${input}`} /></Field>
          <Field label="Default Lead Time"><input data-testid="vendor-leadtime-input" value={form.default_lead_time} onChange={(e) => setForm({ ...form, default_lead_time: e.target.value })} className={input} /></Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={input}>
              <option>Active</option><option>Inactive</option><option>On Hold</option>
            </select>
          </Field>
          <Field label="Shipping Notes" className="sm:col-span-2"><textarea rows={2} value={form.shipping_notes} onChange={(e) => setForm({ ...form, shipping_notes: e.target.value })} className={input} /></Field>
          <Field label="Return Policy Notes" className="sm:col-span-2"><textarea rows={2} value={form.return_policy_notes} onChange={(e) => setForm({ ...form, return_policy_notes: e.target.value })} className={input} /></Field>
          <Field label="Notes" className="sm:col-span-2 lg:col-span-3"><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={input} /></Field>
          <div className="lg:col-span-3">
            <button data-testid="vendor-submit-btn" type="submit" className="rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Save Vendor</button>
          </div>
        </form>
      )}

      {vendors.length === 0 ? (
        <EmptyState testId="vendors-empty" title="No vendors yet" body="Add your trade accounts to link products and procurement." />
      ) : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="vendors-grid">
          {vendors.map((v, i) => (
            <Link key={v.id} to={`/vendors/${v.id}`} data-testid={`vendor-card-${v.id}`} className="card-surface card-hover animate-rise p-6" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-serif text-xl text-ink">{v.name}</h3>
                <Pill tone={v.status === "Active" ? "bg-lime text-ink" : "bg-line text-stone"}>{v.status}</Pill>
              </div>
              <div className="mt-2 overline">{v.vendor_type || "Vendor"}</div>
              <div className="mt-5 space-y-1 text-sm text-stone">
                {v.account_rep && <div>{v.account_rep}</div>}
                {v.email && <div className="truncate">{v.email}</div>}
                {v.default_lead_time && <div className="text-xs text-fog">Lead time · {v.default_lead_time}</div>}
              </div>
              <div className="num mt-5 border-t border-line pt-4 text-xs text-fog">{v.product_count} products sourced</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function VendorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get(`/vendors/${id}`).then((r) => { setVendor(r.data); setForm(r.data); }).catch((e) => toast.error(apiError(e)));
  }, [id]);

  if (!vendor) return <div className="py-24 text-center text-sm text-fog">Loading vendor…</div>;

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put(`/vendors/${id}`, form);
      setVendor(data);
      toast.success("Vendor updated");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-10" data-testid="vendor-detail-page">
      <button onClick={() => navigate("/vendors")} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Vendors
      </button>
      <div className="border-b border-line pb-8">
        <div className="overline mb-3">{vendor.vendor_type || "Vendor"}</div>
        <h1 data-testid="vendor-title" className="font-serif text-3xl tracking-tight text-ink sm:text-4xl">{vendor.name}</h1>
        {vendor.website && <a href={vendor.website} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm text-lime-dark hover:underline">{vendor.website}</a>}
      </div>

      <form onSubmit={save} className="card-surface grid gap-5 p-7 sm:grid-cols-2 lg:grid-cols-3" data-testid="vendor-edit-form">
        {[["name", "Vendor Name"], ["vendor_type", "Vendor Type"], ["website", "Website"], ["account_rep", "Account Rep"], ["email", "Email"], ["phone", "Phone"], ["trade_account_number", "Trade Account Number"], ["default_lead_time", "Default Lead Time"]].map(([k, l]) => (
          <Field key={k} label={l}>
            <input data-testid={`vendor-edit-${k.replace(/_/g, "-")}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={input} />
          </Field>
        ))}
        {[["shipping_notes", "Shipping Notes"], ["return_policy_notes", "Return Policy Notes"], ["notes", "Notes"]].map(([k, l]) => (
          <Field key={k} label={l} className="sm:col-span-2 lg:col-span-1">
            <textarea rows={2} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={input} />
          </Field>
        ))}
        <div className="lg:col-span-3">
          <button data-testid="vendor-save-btn" type="submit" className="rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Save Vendor</button>
        </div>
      </form>

      <div>
        <SectionTitle>Products Sourced</SectionTitle>
        <ProductBoard products={vendor.products || []} emptyTitle="No products from this vendor yet" />
      </div>

      <div>
        <SectionTitle>Procurement History</SectionTitle>
        <div className="card-surface overflow-x-auto scroll-thin">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                {["PO", "Order #", "Status", "Ordered", "Delivered"].map((h) => <th key={h} className="overline px-5 py-3">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {(vendor.procurement || []).map((i) => (
                <tr key={i.id} className="border-b border-line last:border-0 hover:bg-bone">
                  <td className="num px-5 py-3"><Link to={`/procurement/${i.id}`} className="text-ink hover:text-lime-dark">{i.po_number || "—"}</Link></td>
                  <td className="num px-5 py-3 text-stone">{i.vendor_order_number || "—"}</td>
                  <td className="px-5 py-3 text-stone">{i.status}</td>
                  <td className="num px-5 py-3 text-stone">{i.order_date || "—"}</td>
                  <td className="num px-5 py-3 text-stone">{i.actual_delivery_date || "—"}</td>
                </tr>
              ))}
              {!(vendor.procurement || []).length && <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-fog">No procurement history.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-surface p-7">
        <NotesPanel entityType="vendor" entityId={id} title="Vendor Internal Notes" />
      </div>
      <div className="card-surface p-7">
        <SectionTitle>Vendor Documents</SectionTitle>
        <FilesPanel vendorId={id} compact />
      </div>
    </div>
  );
}
